import { z } from "zod";

export const WebProviderIdSchema = z.enum(["deepseek", "parallel", "keenable", "keyless-http"]);
export const PlatformWebConfigurationSchema = z
  .object({
    provider: WebProviderIdSchema,
    enabled: z.boolean(),
    baseUrl: z.string().trim().max(2048).default(""),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.provider !== "deepseek" && value.baseUrl)
      ctx.addIssue({ code: "custom", path: ["baseUrl"], message: "此服务使用内置接口地址" });
    if (value.provider === "deepseek" && value.baseUrl) {
      try {
        const url = new URL(value.baseUrl);
        if (url.protocol !== "https:" || url.username || url.password || url.search || url.hash)
          throw new Error();
      } catch {
        ctx.addIssue({
          code: "custom",
          path: ["baseUrl"],
          message: "请填写不含凭据或查询参数的 HTTPS 地址",
        });
      }
    }
  });
export const PlatformWebInputSchema = z
  .object({
    configuration: PlatformWebConfigurationSchema,
    expectedVersion: z.number().int().nonnegative(),
    keyAction: z.enum(["keep", "replace", "clear"]).default("keep"),
    apiKey: z
      .string()
      .trim()
      .min(1)
      .max(8192)
      .regex(/^[^\r\n]+$/)
      .optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if ((value.keyAction === "replace") !== Boolean(value.apiKey))
      ctx.addIssue({
        code: "custom",
        path: ["apiKey"],
        message: "替换密钥时必须填写新密钥，其他操作不能携带密钥",
      });
    if (value.apiKey && !["deepseek", "keenable"].includes(value.configuration.provider))
      ctx.addIssue({ code: "custom", path: ["apiKey"], message: "此服务不使用密钥" });
  });
export const PlatformWebViewSchema = z.object({
  version: z.number().int().nonnegative(),
  source: z.enum(["deployment", "managed"]),
  hasKey: z.boolean(),
  activeProvider: z.string().nullable(),
  configuration: PlatformWebConfigurationSchema,
});
export const WebRequestErrorCodeSchema = z.enum([
  "http_error",
  "invalid_response",
  "empty_content",
  "invalid_input",
  "network_error",
  "configuration_error",
  "disabled",
  "timeout",
  "provider_error",
  "cancelled",
]);
export const PlatformWebRequestStatusSchema = z.enum([
  "pending",
  "success",
  "empty",
  "error",
  "cancelled",
]);
export const PlatformWebOperationSchema = z.enum(["search", "fetch"]);
export const PlatformWebCursorSchema = z.object({
  createdAt: z.string().datetime(),
  id: z.string().min(1).max(100),
});
export const PlatformWebRequestFilterSchema = z
  .object({
    userId: z.string().trim().min(1).max(100).optional(),
    runId: z.string().trim().min(1).max(100).optional(),
    operation: PlatformWebOperationSchema.optional(),
    provider: WebProviderIdSchema.optional(),
    status: PlatformWebRequestStatusSchema.optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    before: PlatformWebCursorSchema.optional(),
  })
  .strict()
  .refine(
    (v) => !v.from || !v.to || Date.parse(v.from) <= Date.parse(v.to),
    "开始时间不能晚于结束时间",
  )
  .default({});
export const PlatformWebRequestSchema = z.object({
  id: z.string(),
  userId: z.string(),
  spaceId: z.string(),
  botId: z.string().nullable(),
  runId: z.string().nullable(),
  operation: PlatformWebOperationSchema,
  provider: z.string(),
  configurationVersion: z.number().nullable(),
  status: PlatformWebRequestStatusSchema,
  durationMs: z.number().nullable(),
  resultCount: z.number().nullable(),
  contentChars: z.number().nullable(),
  errorCode: WebRequestErrorCodeSchema.nullable(),
  httpStatus: z.number().nullable(),
  createdAt: z.string().datetime(),
  finishedAt: z.string().datetime().nullable(),
});
export const PlatformWebRequestPageSchema = z.object({
  items: z.array(PlatformWebRequestSchema),
  nextCursor: PlatformWebCursorSchema.nullable(),
});
export const PlatformWebRequestContentSchema = z.discriminatedUnion("operation", [
  z.object({ operation: z.literal("search"), query: z.string().max(8192) }).strict(),
  z.object({ operation: z.literal("fetch"), url: z.string().max(8192) }).strict(),
]);
export type PlatformWebConfiguration = z.infer<typeof PlatformWebConfigurationSchema>;
export type PlatformWebInput = z.infer<typeof PlatformWebInputSchema>;
export type PlatformWebView = z.infer<typeof PlatformWebViewSchema>;
export type PlatformWebRequestFilter = z.infer<typeof PlatformWebRequestFilterSchema>;
export type PlatformWebRequestPage = z.infer<typeof PlatformWebRequestPageSchema>;
export type PlatformWebRequest = z.infer<typeof PlatformWebRequestSchema>;
export type PlatformWebRequestContent = z.infer<typeof PlatformWebRequestContentSchema>;
export type WebRequestErrorCode = z.infer<typeof WebRequestErrorCodeSchema>;
